// services/ugc/reactionsApi.ts
import baseApi from "../../lib/baseApi";

// ---------- Types ----------
export type ReactionType =
  | "like"
  | "bless"
  | "gratitude"
  | "amen"
  | "encouragement"
  | "empathy"
  | "faithfire"
  | "support";

// ✅ user shape for serialized CustomUser
export interface SimpleUser {
  id: number;
  username: string;
  name?: string | null;
  family?: string | null;
  avatar_url?: string | null;
  profile_image?: string | null;
  avatar_cdn_url?: string | null;
  profile_url?: string | null;
  is_verified_identity?: boolean;
  is_townlit_verified?: boolean;
  label?: {
    id: number;
    name: string;
    color: string;
  };
}

// ✅ updated ReactionDTO
export interface ReactionDTO {
  id: number;
  reaction_type: ReactionType;
  message?: string | null;
  content_type: string;
  object_id: number;
  timestamp: string;
  name?: number | { id: number; username?: string }; // backward compat
  user?: SimpleUser; // ✅ added
}

export interface ReactionsSummaryItem {
  reaction_type: ReactionType;
  count: number;
}

// ---------- API ----------
export const reactionsApi = {
  // ✅ List all reactions for specific object
  listByObject: async (content_type: string, object_id: number) => {
    const { data } = await baseApi.get<ReactionDTO[]>(
      `/posts/reactions/?content_type=${encodeURIComponent(content_type)}&object_id=${object_id}`
    );
    return data;
  },

  // ✅ Summary counts per reaction_type
  summaryByObject: async (content_type: string, object_id: number) => {
    const { data } = await baseApi.get<ReactionsSummaryItem[]>(
      `/posts/reactions/summary/?content_type=${encodeURIComponent(content_type)}&object_id=${object_id}`
    );
    return data;
  },

  // ✅ List only reactions that include a message (optional filter by type)
  listWithMessage: async (
    content_type: string,
    object_id: number,
    reaction_type?: ReactionType
  ) => {
    const params: Record<string, any> = { content_type, object_id };
    if (reaction_type) params.reaction_type = reaction_type;

    const { data } = await baseApi.get<ReactionDTO[]>(
      `/posts/reactions/with-message/`,
      { params }
    );
    return data;
  },

  // ✅ Add / toggle reaction (with optional message)
  reactToObject: async (payload: {
    content_type: string;
    object_id: number;
    reaction_type: ReactionType;
    message?: string | null;
  }) => {
    const { data, status } = await baseApi.post(`/posts/reactions/`, payload);
    return { data: data as ReactionDTO | undefined, status };
  },

  // ✅ Explicit delete reaction
  removeReaction: async (id: number) => {
    const { status } = await baseApi.delete(`/posts/reactions/${id}/`);
    return status === 204;
  },
};
